import cx from 'classnames';
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type PathValue,
  useController,
  type UseControllerProps,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import ReactPhoneNumberInput, { type Country } from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import {
  COUNTRY_CODE_HINT,
  FALLBACK_PHONE_COUNTRY,
  getInternationalNumberWithoutPlus,
  INVALID_PHONE_MESSAGE,
  isPhoneNumberTooLong,
} from '@shared/utils/phoneCountry';
import typography from '~styles/typography.scss';
import styles from './ControlledFormPhoneInput.scss';
import { FormTooltip } from './FormTooltip';
import { LayoutVariant } from './sharedTypes';
import { ValidationErrorMessage } from './ValidationErrorMessage';
import 'react-phone-number-input/style.css';

export const DEFAULT_COUNTRY: Country = FALLBACK_PHONE_COUNTRY;

export interface ControlledFormPhoneInputProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  autoFocus?: boolean;
  className?: string;
  control: Control<T>;
  customError?: string;
  /** Starting country; staff-facing forms keep the US default */
  defaultCountry?: Country;
  /** Switches country when a country code is typed without a `+` (guest-facing forms only) */
  detectCountryCodeWithoutPlus?: boolean;
  defaultValue?: PathValue<T, Name>;
  disabled?: boolean;
  label: string;
  name: Name;
  placeholder?: string;
  rules?: UseControllerProps<T, Name>['rules'];
  /** Shows "Outside the US? Include your country code" while the US is selected */
  showCountryCodeHint?: boolean;
  tooltipText?: string;
  variant?: LayoutVariant;
  autoComplete?: 'on' | 'off';
  ignoreOnePass?: boolean;
}

export const ControlledFormPhoneInput = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  autoFocus,
  className,
  control,
  customError,
  defaultCountry = DEFAULT_COUNTRY,
  detectCountryCodeWithoutPlus = false,
  label,
  name,
  rules = {},
  disabled,
  placeholder,
  defaultValue,
  showCountryCodeHint = false,
  tooltipText,
  variant = LayoutVariant.Vertical,
  autoComplete = 'off',
  ignoreOnePass = true,
}: ControlledFormPhoneInputProps<T, Name>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    defaultValue,
    name,
    rules: {
      ...rules,
      validate: (newValue: string) =>
        newValue && !!newValue.length
          ? isValidPhoneNumber(newValue) || 'Must be a valid phone number'
          : true,
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  // The library's onChange only gives the parsed E.164 value, so the text the
  // guest actually typed (e.g. "44 20 7946 0999") is read from the input.
  const rawInputRef = useRef('');
  const [country, setCountry] = useState<Country | undefined>(defaultCountry);
  const isTooLong = isPhoneNumberTooLong(rawInputRef.current, value, {
    detectsCountryCodeWithoutPlus: detectCountryCodeWithoutPlus,
  });

  const formOrCustomError =
    error ||
    (customError ? { message: customError, type: 'validate' } : undefined) ||
    (isTooLong
      ? { message: INVALID_PHONE_MESSAGE, type: 'validate' }
      : undefined);

  return (
    <div
      className={cx(styles.phoneNumberInputGroup, styles[variant], className)}
    >
      <div className={styles.labelContainer}>
        <label
          className={cx({
            [styles.labelError]: !!formOrCustomError,
          })}
          htmlFor={name}
        >
          {label}
        </label>
        <FormTooltip label={name} text={tooltipText} />
      </div>
      <div className={styles.phoneInputAndErrorContainer}>
        <div
          className={cx({
            [styles.phoneInputContainer]: true,
            [styles.phoneInputContainerError]: !!formOrCustomError,
          })}
          data-testid="phone-input-container"
          ref={containerRef}
        >
          <ReactPhoneNumberInput
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            data-1p-ignore={ignoreOnePass}
            data-lpignore={ignoreOnePass}
            className={styles.phoneInput}
            data-testid="phone-input"
            defaultCountry={defaultCountry}
            disabled={disabled}
            id={name}
            name={name}
            onChange={(nextValue) => {
              rawInputRef.current =
                containerRef.current?.querySelector('input')?.value ?? '';

              onChange(
                (detectCountryCodeWithoutPlus &&
                  getInternationalNumberWithoutPlus(
                    rawInputRef.current,
                    nextValue,
                  )) ||
                  nextValue,
              );
            }}
            onCountryChange={setCountry}
            placeholder={placeholder || '+1 000 000 0000'}
            // Forms that default the phone to '' would otherwise hand '' back
            // after the library reports undefined (e.g. while only "+43 " is
            // typed), which the library treats as a reset and clears the input
            value={value || undefined}
          />
        </div>
        <ValidationErrorMessage
          error={formOrCustomError}
          label={label}
          name={name}
        />
        {showCountryCodeHint && !formOrCustomError && country === 'US' ? (
          <p className={cx(typography.t2, styles.hint)}>{COUNTRY_CODE_HINT}</p>
        ) : null}
      </div>
    </div>
  );
};
